import React, { useState } from 'react';
import ContactSupportForm from '../components/shared/ContactSupportForm';
import { HelpCircle, ChevronDown, ChevronRight, Mail, Clock, ArrowRight } from 'lucide-react';

const faqs = [
  {
    id: 1,
    question: 'How do I analyze my first document?',
    answer: 'To upload a document, go to Document Analysis, select your analysis type (CV, Contract, or SOP), follow the step-by-step wizard, and click the upload button to select your file. Supported formats include PDF, DOC, DOCX, and TXT.'
  },
  {
    id: 2,
    question: 'What file formats are supported?',
    answer: 'We support PDF (.pdf), Microsoft Word (.doc, .docx), and plain text (.txt) files. Files should be no larger than 10MB for optimal processing speed.'
  },
  {
    id: 3,
    question: 'How accurate is the AI analysis?',
    answer: 'Our AI models achieve 95%+ accuracy on document analysis tasks. However, we recommend reviewing all AI-generated insights and using them as a starting point for your own analysis.'
  },
  {
    id: 4,
    question: 'Can I export my analysis results?',
    answer: 'Yes! After analysis is complete, you can download a comprehensive PDF report containing all insights, risk assessments, and recommendations.'
  },
  {
    id: 5,
    question: 'How do I upgrade my subscription?',
    answer: 'You can upgrade your subscription at any time from your account settings. Your access will continue until the end of your current billing period.'
  }
];

const HelpPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  return (
    <div className="h-full p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-text)] mb-2 leading-tight">
            Help & <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">Support</span>
          </h1>
          <p className="text-lg text-[var(--color-text-muted)]">
            Get the help you need or contact our support team.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start h-full">
          {/* Left: Contact Support */}
          <div className="bg-[var(--color-surface-muted)]/50 border border-[var(--color-border)] rounded-2xl p-10 flex flex-col h-full shadow-lg">
            <div>
              <h2 className="text-2xl font-bold text-[var(--color-text)] mb-8">Contact Support</h2>
              <ContactSupportForm />
            </div>
            <div className="mt-10 pt-6 border-t border-[var(--color-border)]">
              <h3 className="text-lg font-semibold text-[var(--color-text)] mb-3">Other Ways to Reach Us</h3>
              <div className="flex items-center text-[var(--color-text-secondary)] mb-2">
                <Mail className="w-5 h-5 mr-2 text-[var(--color-primary)]" />
                support@docxsai.com
              </div>
              <div className="flex items-center text-[var(--color-text-secondary)]">
                <Clock className="w-5 h-5 mr-2 text-blue-400" />
                We typically respond within 24 hours
              </div>
            </div>
          </div>
          {/* Right: FAQ and More Help */}
          <div className="bg-[var(--color-surface-muted)]/50 border border-[var(--color-border)] rounded-2xl p-10 shadow-lg flex flex-col h-full">
            <h2 className="text-2xl font-bold text-[var(--color-text)] mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4 flex-1">
              {faqs.map((faq) => (
                <div key={faq.id} className="border border-[var(--color-border)] rounded-xl bg-[var(--color-surface)]">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-[var(--color-surface-muted)]/30 transition-colors"
                  >
                    <span className="text-base font-bold text-[var(--color-text)] pr-4">{faq.question}</span>
                    {expandedFaq === faq.id ? (
                      <ChevronDown className="w-5 h-5 text-[var(--color-text-muted)] flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-[var(--color-text-muted)] flex-shrink-0" />
                    )}
                  </button>
                  {expandedFaq === faq.id && (
                    <div className="px-6 pb-5 border-t border-[var(--color-border)] pt-4">
                      <p className="text-[var(--color-text-secondary)] leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Need More Help Box */}
            <div className="mt-8">
              <div className="bg-[var(--color-surface-muted)]/70 rounded-xl p-6 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-[var(--color-text)] mb-1">Need More Help?</h3>
                  <p className="text-[var(--color-text-muted)] text-sm mb-2">Can't find what you're looking for? Our support team is here to help you get the most out of DocxsAi.</p>
                  <a
                    href="mailto:support@docxsai.com"
                    className="brand-gradient-btn inline-flex items-center px-4 py-2 rounded-lg font-medium"
                  >
                    Contact Support <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                </div>
                <HelpCircle className="w-14 h-14 text-[var(--color-primary)]/40 hidden md:block" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpPage; 